import axios from "axios";
import { API_DOMAIN } from "../constants";

export const INITIAL_LOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 60000,
};

export const REFRESH_LOCATION_OPTIONS = {
  enableHighAccuracy: true,
  timeout: 15000,
  maximumAge: 0,
};

const STARTUP_GUARD_PREFIX = "uzdpart360:device-location:startup:";
const LOCATION_ENABLED_USER_TYPES = new Set(["purchaser", "delivery person"]);
export const DESIRED_LOCATION_ACCURACY_M = 100;
export const MAX_LOCATION_ATTEMPTS = 3;
const LOCATION_REFINEMENT_BUDGET_MS = 17500;

export function isDeviceLocationEnabledForUser(user) {
  const userType = String(user?.user_type || "").trim().toLowerCase();
  return LOCATION_ENABLED_USER_TYPES.has(userType);
}

export function mapGeolocationErrorToSafeMessage(error) {
  if (typeof window !== "undefined" && window.isSecureContext === false) {
    return "Location is unavailable because this page is not using HTTPS or localhost.";
  }
  if (error?.code === 1) return "Location permission was denied on this device.";
  if (error?.code === 2) return "This device could not determine its current location.";
  if (error?.code === 3) return "This device did not determine its location in time.";
  return "This device could not provide its current location.";
}

export function locationPayloadFromPosition(position) {
  const latitude = Number(position?.coords?.latitude);
  const longitude = Number(position?.coords?.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error("This device could not provide valid location coordinates.");
  }
  const accuracy = Number(position?.coords?.accuracy);
  return {
    latitude,
    longitude,
    accuracy_m: Number.isFinite(accuracy) ? accuracy : null,
    captured_at: new Date(position.timestamp).toISOString(),
  };
}

function requestSingleBrowserLocation(options) {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this device."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      position => {
        try {
          resolve(locationPayloadFromPosition(position));
        } catch (error) {
          reject(error);
        }
      },
      error => {
        const safeError = new Error(mapGeolocationErrorToSafeMessage(error));
        safeError.geolocationCode = error?.code;
        reject(safeError);
      },
      options,
    );
  });
}

const accuracyRank = location => Number.isFinite(location?.accuracy_m)
  ? location.accuracy_m
  : Number.POSITIVE_INFINITY;

export async function requestBrowserLocation(options = REFRESH_LOCATION_OPTIONS) {
  const deadline = Date.now() + LOCATION_REFINEMENT_BUDGET_MS;
  let bestLocation = null;
  let lastError = null;

  // Browsers often return a quick coarse network fix before their location
  // provider has had time to refine it. Take at most three bounded snapshots
  // and return only the best one. This is not polling or continuous tracking:
  // no watchPosition is registered and the caller still uploads/acknowledges
  // exactly one final location.
  for (let attempt = 0; attempt < MAX_LOCATION_ATTEMPTS; attempt += 1) {
    const remainingMs = deadline - Date.now();
    if (remainingMs <= 250) break;
    try {
      const location = await requestSingleBrowserLocation({
        ...options,
        maximumAge: attempt === 0 ? options.maximumAge : 0,
        timeout: Math.min(Number(options.timeout) || remainingMs, remainingMs),
      });
      if (!bestLocation || accuracyRank(location) < accuracyRank(bestLocation)) bestLocation = location;
      if (accuracyRank(bestLocation) <= DESIRED_LOCATION_ACCURACY_M) return bestLocation;
    } catch (error) {
      lastError = error;
      // Permission denial is definitive for this request; asking again cannot
      // improve it and may create repeated browser prompts.
      if (error?.geolocationCode === 1) throw error;
    }
  }

  if (bestLocation) return bestLocation;
  throw lastError || new Error("This device could not provide its current location.");
}

export async function updateCurrentDeviceLocation(location) {
  const response = await axios.put(`${API_DOMAIN}/auth/device/location`, location);
  return response.data?.data ?? response.data;
}

function startupGuardKey(authState) {
  // Never copy the JWT into another storage key. Modern sessions have a
  // canonical session id; legacy sessions fall back to a non-secret device
  // or user identity and simply attempt once for this browser-tab lifetime.
  const identity = authState?.session_id || authState?.client_device_id || (authState?.id != null ? `legacy-user-${authState.id}` : null);
  return identity ? `${STARTUP_GUARD_PREFIX}${identity}` : null;
}

export function reportInitialDeviceLocationOnce(authState) {
  if (!authState?.isAuthenticated || !isDeviceLocationEnabledForUser(authState?.user)) {
    return Promise.resolve(null);
  }
  const guardKey = startupGuardKey(authState);
  if (!guardKey || sessionStorage.getItem(guardKey)) return Promise.resolve(null);

  // Set the guard before asking the browser so StrictMode/remounts cannot
  // create duplicate prompts or uploads. A denial remains non-blocking and
  // is deliberately not retried during this authenticated browser session.
  sessionStorage.setItem(guardKey, "requested");
  return requestBrowserLocation(INITIAL_LOCATION_OPTIONS)
    .then(updateCurrentDeviceLocation)
    .catch(() => null);
}
